// ParticleWave.js
// 3D "particle ocean" background (no dependencies).
// A perspective field of dots rolling toward a horizon: layered sine swells
// lift and drop each dot, crests glint in the theme accent green, and the
// whole sea pans gently with the mouse. Columns live in world space, so the
// perspective fan-out is real, not faked. Pure 2D canvas, ~1-2ms/frame.
import React, { useEffect, useRef } from "react";

const FOCAL = 420;      // perspective focal length (px)
const CAM_HEIGHT = 300; // camera height above the mean wave plane
const Z_FAR = 2600;     // farthest wave row (shimmers out at the horizon)
const ROWS = 120;       // wave rows between the camera and the horizon
const COL_SPACING = 24; // world units between dots across a row
const AMP = 70;         // swell amplitude (world units)
const DOT = 3;          // dot size at the nearest row (px)
const HORIZON = 0.26;   // horizon line as a fraction of viewport height
const SPEED = 1;        // global wave speed multiplier
const CREST = 0.62;     // normalized height above which a dot glints green
const MAX_DPR = 2;      // cap canvas resolution on high-DPI screens (perf)

// Three layered swells -> height in roughly -1..1
function waveHeight(x, z, t) {
  return (
    Math.sin(x * 0.011 + t) * 0.45 +
    Math.sin(z * 0.008 - t * 0.7) * 0.35 +
    Math.sin((x + z) * 0.0062 + t * 0.45) * 0.2
  );
}

function ParticleWave({ theme }) {
  const canvasRef = useRef(null);
  const themeRef = useRef(theme);
  themeRef.current = theme;
  const drawRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    let width = 0;
    let height = 0;
    let dpr = 1;
    let zNear = 150; // nearest row; recomputed on resize
    let horizonY = 0;
    let mouseX = 0; // normalized -0.5 .. 0.5
    let mouseY = 0;
    let smX = 0; // smoothed mouse (camera follow)
    let smY = 0;
    let t = 0;
    let rafId;

    const frame = () => {
      smX += (mouseX - smX) * 0.06;
      smY += (mouseY - smY) * 0.06;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);

      const accent =
        getComputedStyle(document.documentElement)
          .getPropertyValue("--accent")
          .trim() || "#306230";
      const fg = themeRef.current === "light" ? "#000" : "#fff";

      const cx = width / 2 - smX * 60; // subtle camera pan
      const hy = horizonY + smY * 24; // subtle horizon tilt

      // ~30k dots/frame at this density: only touch fillStyle on change
      let curFill = null;

      for (let iz = 0; iz < ROWS; iz++) {
        const z = zNear + ((Z_FAR - zNear) * iz) / (ROWS - 1);
        const scale = FOCAL / z;

        // distance fade: solid up close, a faint shimmer at the horizon
        const fade = 1 - iz / (ROWS - 1);
        const fadeZ = 0.1 + 0.9 * fade * fade;

        // only iterate the world x-range that actually lands on screen
        const xHalf = (width / 2 + 50) / scale;
        const ix0 = Math.floor(-xHalf / COL_SPACING);
        const ix1 = Math.ceil(xHalf / COL_SPACING);

        for (let ix = ix0; ix <= ix1; ix++) {
          const wx = ix * COL_SPACING;
          const h = waveHeight(wx, z, t);

          const sx = cx + wx * scale;
          const sy = hy + (CAM_HEIGHT - h * AMP) * scale;

          const size = Math.max(DOT * scale, 0.5);
          const crest = (h + 1) * 0.5; // 0 trough .. 1 crest
          ctx.globalAlpha = fadeZ * (0.22 + 0.6 * crest);
          const fill = h > CREST ? accent : fg;
          if (fill !== curFill) {
            ctx.fillStyle = fill;
            curFill = fill;
          }
          ctx.fillRect(sx - size / 2, sy - size / 2, size, size);
        }
      }

      ctx.globalAlpha = 1;
    };
    drawRef.current = frame;

    const loop = () => {
      frame();
      t += 0.02 * SPEED;
      rafId = requestAnimationFrame(loop);
    };

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      horizonY = height * HORIZON;
      // pull the nearest row close enough that swell troughs still cover
      // the bottom edge on tall viewports
      zNear = ((CAM_HEIGHT + AMP) * FOCAL) / (height * (1 - HORIZON) + 60);
      if (reduceMotion) frame();
    };

    const onMouseMove = (e) => {
      mouseX = e.clientX / width - 0.5;
      mouseY = e.clientY / height - 0.5;
    };

    const onTouchMove = (e) => {
      if (e.touches.length > 0) {
        mouseX = e.touches[0].clientX / width - 0.5;
        mouseY = e.touches[0].clientY / height - 0.5;
      }
    };

    resize();
    window.addEventListener("resize", resize);
    if (!reduceMotion) {
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("touchmove", onTouchMove, { passive: true });
      rafId = requestAnimationFrame(loop);
    }

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", resize);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("touchmove", onTouchMove);
      drawRef.current = null;
    };
  }, []);

  // with reduced motion the sea is a still frame, so repaint it on theme flips
  useEffect(() => {
    if (drawRef.current) drawRef.current();
  }, [theme]);

  return <canvas ref={canvasRef} className="particle-wave" aria-hidden="true" />;
}

export default ParticleWave;
